import { AnalyticsContext, OrganisationContext } from '@/context';
import { useBlockedProfileTypes } from '@/hooks';
import { Tag } from '@hogwarts/ui-components-core';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrayParam, useQueryParams, withDefault } from 'use-query-params';
import ProseFilter from '../../../components/ProseFilter';

const ProfilesFilterBar = ({ onFilter }) => {
  const { t } = useTranslation();
  const analytics = useContext(AnalyticsContext);
  const organisation = useContext(OrganisationContext);
  const { profileTypes, ratingSystems, tags } = organisation.scheme;
  const blockedProfileTypes = useBlockedProfileTypes();
  
  const availableProfileTypes = useMemo(() => {
    
    if (!blockedProfileTypes || blockedProfileTypes.length === 0) {
      return profileTypes;
    }
    
    const filtered = profileTypes.filter((pt) => {
      if (blockedProfileTypes.includes(pt.key)) {
        return false;
      }
      if (pt.parent) {
        const parentProfileType = organisation.scheme.getProfileType(pt.parent);
        if (parentProfileType && blockedProfileTypes.includes(parentProfileType.key)) {
          return false;
        }
      }
      return true;
    });
    
    return filtered;
  }, [profileTypes, blockedProfileTypes, organisation.scheme]);

  const queryParams = useMemo(() => {
    const defaultProfileTypes = availableProfileTypes.map((pt) => pt.key);
    
    const queryParams = {
      profileTypes: withDefault(
        ArrayParam,
        defaultProfileTypes
      ),
      // Tags removed from URL - they're still used for filtering but not in the URL
    };
    ratingSystems.forEach((rs) => {
      queryParams[`ratingSystem_${rs.key}`] = ArrayParam;
    });
    return queryParams;
  }, [availableProfileTypes, ratingSystems]);
  const [filterState, setQuery] = useQueryParams(queryParams);
  const ratingSystemSelections = ratingSystems.reduce((prev, curr) => {
    const key = `ratingSystem_${curr.key}`;
    const selections = {
      ...prev,
      [key]: [
        filterState[key]
          ? filterState[key][0] === 'true'
            ? true
            : false
          : false,
      ],
    };
    return selections;
  }, {});
  const filteredSelectedProfileTypes = useMemo(() => {
    if (!filterState.profileTypes || !blockedProfileTypes?.length) {
      return filterState.profileTypes || [];
    }
    return filterState.profileTypes.filter((ptKey) => {
      if (blockedProfileTypes.includes(ptKey)) return false;
      const profileType = organisation.scheme.getProfileType(ptKey);
      if (profileType?.parent) {
        const parentProfileType = organisation.scheme.getProfileType(profileType.parent);
        if (parentProfileType && blockedProfileTypes.includes(parentProfileType.key)) {
          return false;
        }
      }
      return true;
    });
  }, [filterState.profileTypes, blockedProfileTypes, organisation.scheme]);

  const [selectedFilters, setSelectedFilters] = useState({
    profileTypes: filteredSelectedProfileTypes,
    tags: ['active', 'onboarding'], // Default tags, not from URL
    ...ratingSystemSelections,
  });
  const [activeFilters, setActiveFilters] = useState(() => {
    const defaultActiveFilters = ['profileTypes', 'tags'];
    const queryActiveFilters = Object.keys(filterState).filter((filterKey) => {
      return !!filterState[filterKey] && filterKey.startsWith('ratingSystem_');
    });
    return [...defaultActiveFilters, ...queryActiveFilters];
  });
  
  useEffect(() => {
    if (!blockedProfileTypes?.length) {
      return;
    }
    
    const hasBlockedTypes = selectedFilters.profileTypes?.some((ptKey) => {
      if (blockedProfileTypes.includes(ptKey)) return true;
      const profileType = organisation.scheme.getProfileType(ptKey);
      if (profileType?.parent) {
        const parentProfileType = organisation.scheme.getProfileType(profileType.parent);
        if (parentProfileType && blockedProfileTypes.includes(parentProfileType.key)) {
          return true;
        }
      }
      return false;
    });
    
    if (hasBlockedTypes) {      
      const cleanedProfileTypes = selectedFilters.profileTypes.filter((ptKey) => {
        if (blockedProfileTypes.includes(ptKey)) {
          return false;
        }
        const profileType = organisation.scheme.getProfileType(ptKey);
        if (profileType?.parent) {
          const parentProfileType = organisation.scheme.getProfileType(profileType.parent);
          if (parentProfileType && blockedProfileTypes.includes(parentProfileType.key)) {
            return false;
          }
        }
        return true;
      });
      
      if (cleanedProfileTypes.length === 0 && availableProfileTypes.length > 0) {
        setSelectedFilters({
          ...selectedFilters,
          profileTypes: [availableProfileTypes[0].key],
        });
      } else if (cleanedProfileTypes.length !== selectedFilters.profileTypes.length) {
        setSelectedFilters({
          ...selectedFilters,
          profileTypes: cleanedProfileTypes,
        });
      }
    }
  }, [blockedProfileTypes, selectedFilters.profileTypes, organisation.scheme, availableProfileTypes]);
  
  useEffect(
    () => {
      let profileTypesForUrl = selectedFilters.profileTypes || [];
      
      if (blockedProfileTypes?.length && profileTypesForUrl.length > 0) {
        profileTypesForUrl = profileTypesForUrl.filter((ptKey) => {
          if (blockedProfileTypes.includes(ptKey)) {
            return false;
          }
          const profileType = organisation.scheme.getProfileType(ptKey);
          if (profileType?.parent) {
            const parentProfileType = organisation.scheme.getProfileType(profileType.parent);
            if (parentProfileType && blockedProfileTypes.includes(parentProfileType.key)) {
              return false;
            }
          }
          return true;
        });
      
        if (profileTypesForUrl.length === 0 && availableProfileTypes.length > 0) {
          profileTypesForUrl = [availableProfileTypes[0].key];
        }
      }
      
      const currentFilters = {};
      const filtersForQuery = {};
      for (const filterKey of Object.keys(selectedFilters)) {
        if (filterKey === 'tags') {
          currentFilters[filterKey] = selectedFilters[filterKey];
          filtersForQuery[filterKey] = undefined; // Don't include in URL
        } else if (filterKey === 'profileTypes') {
          if (activeFilters.includes(filterKey)) {
            currentFilters[filterKey] = profileTypesForUrl;
            filtersForQuery[filterKey] = profileTypesForUrl;
          } else {
            filtersForQuery[filterKey] = undefined;
          }
        } else if (activeFilters.includes(filterKey)) {
          currentFilters[filterKey] = selectedFilters[filterKey];
          filtersForQuery[filterKey] = selectedFilters[filterKey];
        } else {
          filtersForQuery[filterKey] = undefined;
        }
      }
      onFilter(currentFilters);
      setQuery(filtersForQuery, 'replace');
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeFilters, selectedFilters, blockedProfileTypes, availableProfileTypes]
  );

  const terms = { prefix: t('Showing') };
  const filters = [
    {
      id: 'profileTypes',
      name: t('Profile Types'),
      available: availableProfileTypes.map((pt) => ({
        id: pt.key,
        label: pt.label,
      })),
      selected: selectedFilters.profileTypes,
      selectOptions: {
        min: 1,
        max: null,
        removable: false,
        withApply: true,
      },
      terms: {
        allSelected: t('all profile types'),
        joining: t('of type'),
        prefix: null,
        suffix: null,
      },
    },
    {
      id: 'tags',
      name: t('Tags'),
      component: Tag,
      available: tags.map((t) => ({
        id: t.key,
        label: t.label,
        color: t.color,
      })),
      selected: selectedFilters.tags,
      selectOptions: {
        min: 1,
        max: null,
        removable: false,
        withApply: true,
      },
      terms: {
        allSelected: null,
        joining: t('with'),
        prefix: null,
        suffix: t('tags'),
      },
    },
    ...ratingSystems.map((rs, idx) => ({
      id: `ratingSystem_${rs.key}`,
      name: rs.label,
      available: [
        {
          id: false,
          label: `${t('Not')} ${rs.label}`,
        },
        {
          id: true,
          label: rs.label,
        },
      ],
      selected: selectedFilters[`ratingSystem_${rs.key}`],
      selectOptions: {
        min: 1,
        max: 1,
        removable: true,
      },
      terms: {
        allSelected: null,
        joining: idx === 0 ? t('that are') : t('and'),
        prefix: null,
        suffix: null,
      },
    })),
  ];

  return (
    <ProseFilter
      terms={terms}
      filters={filters}
      activeFilters={activeFilters}
      onSelectionChange={(type, selected) => {
        const newFilters = {
          ...selectedFilters,
          [type]: selected,
        };
        analytics.events.profiles.filtersChanged({
          newFilters,
          activeFilters,
        });
        setSelectedFilters(newFilters);
      }}
      onAddFilter={(filterId) => {
        analytics.events.profiles.filtersAdded({ filterId });
        setActiveFilters([...activeFilters, filterId]);
      }}
      onRemoveFilter={(filterId) => {
        analytics.events.profiles.filtersRemoved({ filterId });
        const newFilters = activeFilters.filter((f) => f !== filterId);
        setActiveFilters(newFilters);
      }}
    />
  );
};

export default ProfilesFilterBar;
